import { useState, useMemo } from 'react';
import { 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  TextField, 
  Typography, 
  Alert, 
  Box, 
  Button,
  CircularProgress,
  Snackbar,
  Link
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import type { ConversionResult } from '../types/documenso';
import type { ReplacementVariable, EventData } from '../utils/documensoConverter';
import { documensoApi } from '../api';

interface DocumensoExportPanelProps {
  conversionResult: ConversionResult;
  onReplacementVariablesChange?: (variables: ReplacementVariable[] | undefined) => void;
  onEventDataChange?: (eventData: EventData | undefined) => void;
  templateId: string;
  version: number;
}

export function DocumensoExportPanel({ conversionResult, onReplacementVariablesChange, onEventDataChange, templateId, version }: DocumensoExportPanelProps) {
  const [eventData, setEventData] = useState(JSON.stringify(dummyData));
  const [parseError, setParseError] = useState<string | null>(null);
  
  // API request state
  const [sending, setSending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [signingUrl, setSigningUrl] = useState<string | null>(null);

  // Parse event data and extract replacement variables
  const { replacementVariables, parsedEventData } = useMemo(() => {
    if (!eventData.trim()) {
      setParseError(null);
      return { replacementVariables: undefined, parsedEventData: undefined };
    }

    try {
      const parsed = JSON.parse(eventData);
      
      // Check if replacementVariables array exists
      if (!parsed.replacementVariables || !Array.isArray(parsed.replacementVariables)) {
        setParseError('Event data must contain a "replacementVariables" array');
        return { replacementVariables: undefined, parsedEventData: undefined };
      }

      // Validate structure of replacement variables
      const variables: ReplacementVariable[] = parsed.replacementVariables.map((v: any, index: number) => {
        if (!v.name || (v.content === undefined && v.content !== false)) {
          throw new Error(`Invalid replacement variable at index ${index}: must have "name" and "content" fields`);
        }
        return {
          name: v.name,
          content: v.content,
        };
      });

      setParseError(null);
      return { replacementVariables: variables, parsedEventData: parsed as EventData };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Invalid JSON format';
      setParseError(errorMessage);
      return { replacementVariables: undefined, parsedEventData: undefined };
    }
  }, [eventData]);

  // Notify parent when replacement variables change
  useMemo(() => {
    if (onReplacementVariablesChange) {
      onReplacementVariablesChange(replacementVariables);
    }
  }, [replacementVariables, onReplacementVariablesChange]);

  // Notify parent when event data changes
  useMemo(() => {
    if (onEventDataChange) {
      onEventDataChange(parsedEventData);
    }
  }, [parsedEventData, onEventDataChange]);
  
  const jsonString = conversionResult.success && conversionResult.payload
    ? JSON.stringify(conversionResult.payload, null, 2)
    : '';

  const handleSubmitTest = async () => {
    if (!conversionResult.success || !conversionResult.payload) {
      setSnackbarMessage('No valid payload to send');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setSending(true);
    setSigningUrl(null);

    try {
      const result = await documensoApi.submitTest(templateId, version, parsedEventData);
      
      if (result.signingUrl) {
        setSigningUrl(result.signingUrl);
        setSnackbarMessage('Test-Antrag erstellt!');
      } else {
        setSnackbarMessage(`Envelope created! ID: ${result.envelopeId}`);
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setSnackbarMessage(`Failed: ${errorMessage}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Accordion defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Documenso Export (Debug)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Action Button */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
                onClick={handleSubmitTest}
                disabled={sending || !conversionResult.success}
                size="small"
                title="Erstelle Test-Antrag"
              >
                {sending ? 'Sende...' : 'Erstelle Test-Antrag'}
              </Button>
            </Box>

            {/* Signing URL display */}
            {signingUrl && (
              <Alert severity="success">
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Signing URL:
                </Typography>
                <Link href={signingUrl} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                  Open
                </Link>
                <Typography variant="caption" display="block" sx={{ mt: 0.5, wordBreak: 'break-all', color: 'text.secondary' }}>
                  {signingUrl}
                </Typography>
              </Alert>
            )}

            {/* Event Data Input */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Event Data (for field population):
              </Typography>
              <TextField
                multiline
                fullWidth
                value={eventData}
                onChange={(e) => setEventData(e.target.value)}
                placeholder='Paste event JSON here (e.g., {"replacementVariables": [...]})'
                InputProps={{
                  style: { fontFamily: 'monospace', fontSize: '12px' },
                }}
                minRows={6}
                maxRows={12}
                variant="outlined"
                error={!!parseError}
                helperText={parseError || "Paste the event object with replacementVariables to populate field values"}
              />
              {parseError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {parseError}
                </Alert>
              )}
            </Box>

            {/* Documenso Payload Output */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Documenso API Payload:
              </Typography>
              {conversionResult.success ? (
                <TextField
                  multiline
                  fullWidth
                  value={jsonString}
                  InputProps={{
                    readOnly: true,
                    style: { fontFamily: 'monospace', fontSize: '12px' },
                  }}
                  minRows={10}
                  maxRows={20}
                  variant="outlined"
                  helperText="Copy this JSON to use with Documenso API"
                />
              ) : (
                <Alert severity="error">
                  <Typography variant="body2">
                    <strong>Conversion Error:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {conversionResult.error}
                  </Typography>
                </Alert>
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbarSeverity} 
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}



const dummyData = {
  "ZZVLeadObject": {
    "uuid": "568476de-ee53-4793-b73b-76388324aaaa",
    "gacid": false,
    "versicherte_person_type": "DIFFERENT",
    "request_type": "DIFFERENT_EMAIL_KIDS",
    "id": "ZZV-A274037",
    "status": "IN PROGRESS",
    "price": "17,16 €",
    "tarif_id": "1650153",
    "gclid": "CjwKCAjwpcTNBhA5EiwAdO1S9svlH8Nm337m820xhno5QKHZcUMZtTZa3bCQvjKfXL0s64GiMXGqpBoCl8kQAvD_XxX",
    "rqid": "ed6e9f63-fa55-490d-a9cc-f2c65cbfc689",
    "utm": {
      "utm_source": "google",
      "utm_medium": "cpc",
      "utm_campaign": "mobile_brand_ukv"
    },
    "abTest": "23",
    "abGroup": "false",
    "healthQuestions": {
      "HEALTHQUESTION_PLANNEDTREATMENT": null,
      "HEALTHQUESTION_PLANNEDTREATMENT_DENTURES": null,
      "HEALTHQUESTION_PLANNEDTREATMENT_ABOVE2Y": null,
      "HEALTHQUESTION_DENTGAP": null,
      "HEALTHQUESTION_DENTGAP_AMOUNT": null,
      "HEALTHQUESTION_DENTURE": null,
      "HEALTHQUESTION_DENTURE_AMOUNT": null,
      "PREFERENCE_PLANNEDTREATMENT_INSURED": null,
      "PREFERENCE_DENTGAP_INSURED": null,
      "PREFERENCE_DENTURE_INSURED": null
    },
    "priceItemDetails": {
      "id": "1650153",
      "price": 17.16,
      "dure": {
        "bok": true,
        "con": null,
        "cot": "absolute",
        "inc": null,
        "ins": true,
        "max": null
      },
      "dgap": {
        "bok": true,
        "con": 9,
        "cot": "absolute",
        "inc": null,
        "ins": true,
        "max": 3
      },
      "ss": null,
      "sst": null
    },
    "antragsteller_person": {
      "a_firstname": "Karen",
      "a_lastname": "Trommer",
      "a_fullname": "Trommer, Karen",
      "a_sex": "Frau",
      "a_title": "Frau",
      "a_street": false,
      "a_zip": false,
      "a_city": false,
      "a_email": "van.den.eijkel@gmail.com",
      "a_phone": "+4915904303937",
      "a_birthday": "12.11.1985"
    },
    "versicherte_person": {
      "v_firstname": "Adrian",
      "v_lastname": "Trommer",
      "v_sex": "Herr",
      "v_title": "Herr",
      "v_street": false,
      "v_zip": false,
      "v_city": false,
      "v_email": false,
      "v_phone": false,
      "v_birthday": "29.08.2012",
      "v_start": "01.04.2026",
      "v_tarifid": "1650153"
    },
    "zahler_person": {
      "z_name": "Karen Trommer",
      "z_iban": false,
      "z_bic": false,
      "z_email": "van.den.eijkel@gmail.com",
      "z_bankname": false
    },
    "created_date": "2026-03-12T05:14:49.608Z",
    "antragsart": "EMAIL",
    "schemaVersion": 3,
    "token": "NONE",
    "price_raw": "17.16",
    "execution_id": "17028d0a-9b73-4570-a953-8b3e04d1baaa",
    "createdYYYYMMDD": "20260312",
    "createdYYYYMM": "202603",
    "createdYYYY": "2026",
    "firewall_blocked": false
  },
  "lead_id": "ZZV-A123456",
  "replacementVariables": [
    {
      "name": "a_firstname",
      "content": "Karen"
    },
    {
      "name": "a_lastname",
      "content": "Trommer"
    },
    {
      "name": "a_fullname",
      "content": "Trommer, Karen"
    },
    {
      "name": "a_sex",
      "content": "Frau"
    },
    {
      "name": "a_title",
      "content": "Frau"
    },
    {
      "name": "a_street",
      "content": false
    },
    {
      "name": "a_zip",
      "content": false
    },
    {
      "name": "a_city",
      "content": false
    },
    {
      "name": "a_email",
      "content": "van.den.eijkel@gmail.com"
    },
    {
      "name": "a_phone",
      "content": "+4915904303937"
    },
    {
      "name": "a_birthday",
      "content": "12.11.1985"
    },
    {
      "name": "v_firstname",
      "content": "Adrian"
    },
    {
      "name": "v_lastname",
      "content": "Trommer"
    },
    {
      "name": "v_sex",
      "content": "Herr"
    },
    {
      "name": "v_title",
      "content": "Herr"
    },
    {
      "name": "v_street",
      "content": false
    },
    {
      "name": "v_zip",
      "content": false
    },
    {
      "name": "v_city",
      "content": false
    },
    {
      "name": "v_email",
      "content": false
    },
    {
      "name": "v_phone",
      "content": false
    },
    {
      "name": "v_birthday",
      "content": "29.08.2012"
    },
    {
      "name": "v_start",
      "content": "01.04.2026"
    },
    {
      "name": "v_tarifid",
      "content": "1650153"
    },
    {
      "name": "z_name",
      "content": "Karen Trommer"
    },
    {
      "name": "z_iban",
      "content": false
    },
    {
      "name": "z_bic",
      "content": false
    },
    {
      "name": "z_email",
      "content": "karentrommer1211@gmail.com"
    },
    {
      "name": "z_bankname",
      "content": false
    },
    {
      "name": "price",
      "content": "17,16 €"
    },
    {
      "name": "current_date",
      "content": "12.03.2026"
    },
    {
      "name": "lead_id",
      "content": "ZZV-A274037"
    },
    {
      "name": "VERSICHERUNG",
      "content": "UKV"
    },
    {
      "name": "TARIF",
      "content": "ZahnPRIVAT 90"
    }
  ],
  "automationProvider": "DocuSign",
  "automationProviderSettings": {
    "templateId": {
      "SAME": "4c164649-5f29-4413-9c55-209aadda4d1f",
      "DIFFERENT_POST_KIDS": "39c29aa5-8b2e-465d-ae03-f9e0de592208",
      "DIFFERENT": "7c43470d-c001-4317-b168-1a384c055eb8",
      "DIFFERENT_EMAIL_KIDS": "39c29aa5-8b2e-465d-ae03-f9e0de592208"
    }
  },
  "beratungsprotokoll": {
    "Key": "Beratungsprotokoll/ZZV-A274037.pdf",
    "Bucket": "vdd-zzv-pdfantrag"
  }
};